import { useEffect, type ReactElement } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  canAccessPath,
  getDefaultRedirectPath,
} from "./utils/userTypePermissions";
import { UNAUTHORIZED, LANDING, HOME } from "./routes/paths";
import { useAppSelector } from "./store/hooks";

const ProtectedRoute = ({
  isLoggedIn,
  children,
  isLoading,
  hasError,
}: {
  isLoggedIn: boolean;
  children: ReactElement;
  isLoading: boolean;
  hasError: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useAppSelector((state) => state.user);

  useEffect(() => {
    if (!isLoading && !isLoggedIn && !hasError) {
      // Check if this is an OAuth callback
      const urlParams = new URLSearchParams(window.location.search);
      const hasOAuthCode = urlParams.has("code");

      // Don't redirect during OAuth callback processing
      if (hasOAuthCode) {
        return;
      }

      // Since we're already in a protected route and not authenticated,
      // let the main App component handle the login redirect
      // Don't force logout here as it creates the immediate logout issue
    }

    // Check user type permissions and handle redirects after user is loaded
    if (!isLoading && isLoggedIn && userInfo?.userTypeName) {
      const userTypeName = userInfo.userTypeName;
      const currentPath = location.pathname;

      // Handle PlusCard user redirect to /plus-card on initial login
      if (userTypeName === "PlusCard") {
        const defaultPath = getDefaultRedirectPath(userTypeName);

        // If user is on landing page or root, redirect to plus-card
        if (currentPath === HOME || currentPath === LANDING) {
          void navigate(defaultPath, { replace: true });
          return;
        }
      }

      // Check if user has access to current path
      if (!canAccessPath(userTypeName, currentPath)) {
        // Redirect to unauthorized page
        void navigate(UNAUTHORIZED, { replace: true });
      }
    }
  }, [
    isLoading,
    isLoggedIn,
    hasError,
    navigate,
    location.pathname,
    userInfo?.userTypeName,
  ]);

  // Show children if logged in
  if (isLoggedIn) {
    return children;
  }

  return null;
};

export default ProtectedRoute;
